// Add photos to a job sheet from the office (Session ABD Loop 3), reusing the same upload endpoint
// the engineer PWA uses and the shared JobPhotoGallery for display + delete. Used at manual-record
// creation and in the service-record panel, so a sheet can carry photos from the point of creation,
// not only after. Photos captured on the PWA already carry a geotag + timestamp caption on the
// rendered sheet; office-added photos have no capture metadata, so they render without a caption
// (honest).
//
// Session ADE: the whole section is a DROP ZONE (drag files from the desktop; click-to-browse
// stays), and the gallery is drag-sortable -- the persisted order is the JobSheet.photos array
// itself, saved via the reorder endpoint and honoured by the proxy indices and the PDFs. File
// drops are native HTML5 drag events; the reorder is @dnd-kit pointer-based, so the two gestures
// can never swallow each other (a file drag from the OS never starts a tile drag, and vice versa).
import { useRef, useState } from "react";
import { api, ApiError } from "../../lib/api";
import { dragHasFiles } from "../../lib/photoDrag";
import { JobPhotoGallery } from "./JobPhotoGallery";
import { isImageFile, prepareImageForUpload } from "../../lib/imageUpload";

// A stable-ish per-photo id so the upload is idempotent server-side (a lost response does not
// duplicate the photo). Uses the file name + size + time; good enough for the office single-user path.
function makePhotoId(file: File): string {
  const stamp = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 8);
  return `office-${stamp}-${rand}-${file.size}`;
}

export function JobPhotoUploader({
  jobId,
  initialCount = 0,
  label = "Photos",
  onCountChange,
  identify,
}: {
  jobId: string;
  initialCount?: number;
  label?: string;
  onCountChange?: (count: number) => void;
  // Passed through to the gallery: each thumbnail offers "Identify this part" (Session AP).
  identify?: { source: "jobsheet" | "project"; id: string };
}) {
  const [count, setCount] = useState(initialCount);
  const [version, setVersion] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  // Depth counter: dragenter/dragleave fire for every child the file drag crosses, so the
  // highlight only clears when the drag genuinely leaves the whole zone.
  const dragDepth = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);

  async function upload(files: FileList | File[] | null) {
    const list = files ? Array.from(files) : [];
    if (list.length === 0) return;
    const images = list.filter(isImageFile);
    if (images.length === 0) {
      setError("Only image files can be uploaded.");
      return;
    }
    setBusy(true);
    setError(images.length < list.length ? "Only the image files were uploaded (other file types are skipped)." : null);
    let latest = count;
    try {
      for (const original of images) {
        const file = await prepareImageForUpload(original);
        const res = await api.jobs.uploadPhoto(jobId, makePhotoId(file), file, file.name);
        latest = res.photos.length;
      }
      setCount(latest);
      setVersion((v) => v + 1);
      onCountChange?.(latest);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 503
          ? "Google Drive is not connected, so photos cannot be uploaded yet."
          : err instanceof ApiError && err.status === 415
            ? "Only image files can be uploaded."
            : err instanceof ApiError && err.status === 413
              ? "A photo was too large to upload."
              : "Could not upload the photo. Please try again.",
      );
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = ""; // allow re-selecting the same file
    }
  }

  async function deletePhoto(index: number) {
    try {
      const res = await api.jobs.deletePhoto(jobId, index);
      setCount(res.photos.length);
      setVersion((v) => v + 1);
      onCountChange?.(res.photos.length);
    } catch {
      setError("Could not delete the photo. Please try again.");
    }
  }

  // Persist a drag-reordered display order. On success the indices have changed, so bump the
  // version and every tile refetches by its new index. A 409 means the photos changed underneath
  // (another device uploaded/deleted); refresh the view rather than guessing.
  async function reorder(order: number[]) {
    setError(null);
    try {
      const res = await api.jobs.reorderPhotos(jobId, order);
      setCount(res.photos.length);
      setVersion((v) => v + 1);
      onCountChange?.(res.photos.length);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError("The photos changed while you were reordering, so the order was not saved. The view has been refreshed; please try again.");
        setVersion((v) => v + 1);
      } else {
        setError("Could not save the new photo order. Please try again.");
      }
    }
  }

  return (
    <div
      className={`mt-2 rounded-ele border-2 border-dashed p-2 transition-colors ${
        dragOver ? "border-ele-green bg-ele-green/5" : "border-transparent"
      }`}
      onDragEnter={(e) => {
        if (!dragHasFiles(e.dataTransfer)) return;
        e.preventDefault();
        dragDepth.current += 1;
        setDragOver(true);
      }}
      onDragOver={(e) => {
        if (!dragHasFiles(e.dataTransfer)) return;
        e.preventDefault(); // required, or the browser navigates to the dropped file
      }}
      onDragLeave={(e) => {
        if (!dragHasFiles(e.dataTransfer)) return;
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0) setDragOver(false);
      }}
      onDrop={(e) => {
        if (!dragHasFiles(e.dataTransfer)) return;
        e.preventDefault();
        dragDepth.current = 0;
        setDragOver(false);
        if (!busy) void upload(e.dataTransfer.files);
      }}
    >
      <div className="mb-2 flex flex-wrap items-center gap-3">
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-ele border border-ele-blue px-3 py-1.5 font-menu text-xs tracking-wide text-ele-blue transition hover:bg-ele-inset">
          {busy ? "Uploading..." : "Add photos"}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            disabled={busy}
            onChange={(e) => upload(e.target.files)}
            className="hidden"
            aria-label="Add photos"
          />
        </label>
        <span className="ele-desktop-only text-xs text-ele-body opacity-70">{dragOver ? "Drop the photos to upload them" : "or drag photos here from your desktop"}</span>
        {count > 0 && <span className="text-xs text-ele-body opacity-70">{count} on file</span>}
      </div>
      {error && <p role="alert" className="mb-2 text-xs text-ele-danger">{error}</p>}
      <JobPhotoGallery
        jobId={jobId}
        count={count}
        label={label}
        emptyText="No photos yet."
        version={version}
        onDeletePhoto={deletePhoto}
        identify={identify}
        onReorder={(order) => void reorder(order)}
      />
    </div>
  );
}
